// macOS drags a picture-in-picture window when the gesture is not a native
// scroll. Leave the event alone while a scroller can still move. Cancel it
// only when nothing can move, so the leftover gesture does not drag the window.

export function nearestScrollable(
  target: EventTarget | null,
  deltaX: number,
  deltaY: number,
): Element | null {
  let node = asElement(target)
  while (node) {
    if (canTakeWheel(node, deltaX, deltaY)) {
      return node
    }
    node = node.parentElement
  }
  return null
}

export function bindPictureInPictureScroll(pipDocument: Document): () => void {
  const onWheel = (event: WheelEvent) => {
    if (nearestScrollable(event.target, event.deltaX, event.deltaY)) {
      return
    }
    event.preventDefault()
  }
  const touch = { id: -1, x: 0, y: 0, active: false }
  const onTouchStart = (event: TouchEvent) => {
    const point = event.changedTouches[0]
    if (!point) {
      return
    }
    touch.id = point.identifier
    touch.x = point.clientX
    touch.y = point.clientY
    touch.active = true
  }
  const onTouchMove = (event: TouchEvent) => {
    if (!touch.active) {
      return
    }
    const point = findTouch(event.changedTouches, touch.id) ?? event.changedTouches[0]
    if (!point) {
      return
    }
    const deltaX = touch.x - point.clientX
    const deltaY = touch.y - point.clientY
    touch.x = point.clientX
    touch.y = point.clientY
    if (nearestScrollable(event.target, deltaX, deltaY)) {
      return
    }
    event.preventDefault()
  }
  const onTouchEnd = (event: TouchEvent) => {
    if (!touch.active || !findTouch(event.changedTouches, touch.id)) {
      return
    }
    touch.active = false
  }

  pipDocument.addEventListener('wheel', onWheel, { capture: true, passive: false })
  pipDocument.addEventListener('touchstart', onTouchStart, { capture: true, passive: true })
  pipDocument.addEventListener('touchmove', onTouchMove, { capture: true, passive: false })
  pipDocument.addEventListener('touchend', onTouchEnd, { capture: true })
  pipDocument.addEventListener('touchcancel', onTouchEnd, { capture: true })

  return () => {
    pipDocument.removeEventListener('wheel', onWheel, { capture: true })
    pipDocument.removeEventListener('touchstart', onTouchStart, { capture: true })
    pipDocument.removeEventListener('touchmove', onTouchMove, { capture: true })
    pipDocument.removeEventListener('touchend', onTouchEnd, { capture: true })
    pipDocument.removeEventListener('touchcancel', onTouchEnd, { capture: true })
  }
}

function asElement(target: EventTarget | null): Element | null {
  if (!target || typeof target !== 'object' || !('nodeType' in target)) {
    return null
  }
  const node = target as Node
  if (node.nodeType === Node.ELEMENT_NODE) {
    return node as Element
  }
  return node.parentElement
}

function canTakeWheel(element: Element, deltaX: number, deltaY: number): boolean {
  return scrollsOn(element, 'y', deltaY) || scrollsOn(element, 'x', deltaX)
}

function scrollsOn(element: Element, axis: 'x' | 'y', delta: number): boolean {
  if (delta === 0) {
    return false
  }
  const view = element.ownerDocument.defaultView
  if (!view) {
    return false
  }
  const style = view.getComputedStyle(element)
  const overflow = axis === 'y' ? style.overflowY : style.overflowX
  if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'overlay') {
    return false
  }
  const position = axis === 'y' ? element.scrollTop : element.scrollLeft
  const size = axis === 'y' ? element.clientHeight : element.clientWidth
  const extent = axis === 'y' ? element.scrollHeight : element.scrollWidth
  if (extent - size <= 1) {
    return false
  }
  if (delta > 0) {
    return position + size < extent - 1
  }
  return position > 0
}

function findTouch(touches: ArrayLike<Touch>, id: number): Touch | null {
  for (const point of Array.from(touches)) {
    if (point.identifier === id) {
      return point
    }
  }
  return null
}
